import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Metadata } from '@app/core/interfaces/metadata';
import { MetaService } from '@app/core/services/meta.service';
import { GalleryItemsLoadMoreComponent } from '@app/entities/images/components/gallery-items-load-more/gallery-items-load-more.component';
import { GalleryFiltersAndSorting } from '@app/entities/images/interfaces/gallery-filters-and-sorting';
import { ImagePreview } from '@app/entities/images/interfaces/image-preview';
import { GalleryComponent } from '@app/layout/gallery/gallery.component';
import {
  GallerySelectors,
  LoadGallery,
  PopulateFilters,
  SetContentTypeFilterValue,
  SetGalleryCategory,
  SetOrientationFilterValue,
  SetPricingFilterValue,
  SetSearchRequestValue,
  SetSortingOptionsValue,
} from '@app/store';
import { Store } from '@ngxs/store';
import { Observable, tap } from 'rxjs';
import { ContentTypeFilterComponent } from './content-type-filter/content-type-filter.component';
import { GalleryTagsComponent } from './gallery-tags/gallery-tags.component';
import { GalleryTitleComponent } from './gallery-title/gallery-title.component';
import { OrientationFilterValues } from './orientation-filter/orientation-filter-values';
import { OrientationFilterComponent } from './orientation-filter/orientation-filter.component';
import { PricingFilterValues } from './pricing-filter/pricing-filter-values';
import { PricingFilterComponent } from './pricing-filter/pricing-filter.component';
import { SortingOptionsValues } from './sorting-options/sorting-options-values';
import { SortingOptionsComponent } from './sorting-options/sorting-options.component';

@Component({
  selector: 'app-gallery-page',
  templateUrl: './gallery.page.html',
  styleUrls: ['./gallery.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    GalleryComponent,
    GalleryTitleComponent,
    GalleryTagsComponent,
    PricingFilterComponent,
    ContentTypeFilterComponent,
    OrientationFilterComponent,
    SortingOptionsComponent,
    AsyncPipe,
    GalleryItemsLoadMoreComponent,
  ],
})
export class GalleryPage implements OnInit {
  protected keyword: string = '';

  constructor(
    private activatedRoute: ActivatedRoute,
    private store: Store,
    private destroyRef: DestroyRef,
    private metaService: MetaService,
  ) {}

  protected images$: Observable<ImagePreview[] | undefined> = this.store.select(
    GallerySelectors.images,
  );

  protected hasMoreResults$: Observable<boolean> = this.store.select(
    GallerySelectors.hasMoreResults,
  );

  ngOnInit(): void {
    this.handleFilterChanges();
  }

  private handleFilterChanges(): void {
    this.activatedRoute.queryParams
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((data: GalleryFiltersAndSorting) => {
          this.store.dispatch(new PopulateFilters(data));
          this.store.dispatch(
            new SetPricingFilterValue(data.is_free || PricingFilterValues.ALL),
          );
          this.store.dispatch(new SetContentTypeFilterValue(data.type || ''));
          this.store.dispatch(
            new SetOrientationFilterValue(
              data.orientation || OrientationFilterValues.ALL,
            ),
          );
          this.store.dispatch(
            new SetSortingOptionsValue(
              data.ordering || SortingOptionsValues.LATEST,
            ),
          );

          this.keyword =
            this.prepareKeyword(data.search) ||
            this.prepareKeyword(data.category);

          this.updateMeta();
          this.store.dispatch(new SetSearchRequestValue(data.search || ''));
          this.store.dispatch(new SetGalleryCategory(data.category));
          this.loadGallery();
        }),
      )
      .subscribe();
  }

  private buildTitle(searchQuery: string): string {
    return `Buy ${searchQuery} Stock Videos | Download ${searchQuery} images in high-resolution and RAW format - videosense.net`.replace(
      /\s{2,}/g,
      ' ',
    );
  }

  private buildMetaDescription(searchQuery: string): string {
    return `Explore and buy premium ${searchQuery} stock videos in high-resolution and RAW format on videosense.neto. Perfect for professional projects, websites, blogs, and ads. Discover breathtaking ${searchQuery} videos today!`.replace(
      /\s{2,}/g,
      ' ',
    );
  }

  private buildMetaKeywords(searchQuery: string): string {
    return [
      `Buy ${searchQuery} stock videos`,
      `${searchQuery} videos`,
      `${searchQuery} RAW videos`,
      `${searchQuery} high-resolution videos`,
      `high-quality ${searchQuery} pictures`,
      `${searchQuery} videography`,
      `premium ${searchQuery} stock videos`,
      `${searchQuery} 4k videos`,
      `${searchQuery} animal videos`,
      `${searchQuery} close-up videos`,
      `professional ${searchQuery} videos`,
      `${searchQuery} for creative projects`,
      'videosense.net.',
    ]
      .join(', ')
      .replace(/\s{2,}/g, ' ');
  }

  private prepareKeyword(keyword?: string): string {
    // Trim the search string or initialize as an empty string if undefined
    keyword = keyword ? keyword.trim() : '';

    // Return early if the trimmed string is empty
    if (!keyword) {
      return keyword;
    }

    // Capitalize the first letter of each word and return the result
    return keyword.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private updateMeta(): void {
    const metadata: Metadata = {
      title: this.buildTitle(this.keyword),
      description: this.buildMetaDescription(this.keyword.toLowerCase()),
      keywords: this.buildMetaKeywords(this.keyword.toLowerCase()),
    };
    this.metaService.setMetadata(metadata);
  }

  private loadGallery(): void {
    this.store.dispatch(new LoadGallery());
  }

  protected onLoadMore(): void {
    this.store.dispatch(new LoadGallery(true));
  }
}
